import { useEffect, useRef, useState } from 'react';
import {
  Activity, Lightbulb, Quote, Navigation, Share2, Layers, Atom, Users,
  Sparkles, Download, Upload, FileText, X, ChevronRight, Info, Square,
  type LucideIcon,
} from 'lucide-react';
import { color, mono } from './tokens';
import { apiClient } from '../../api';
import { useProjectIngestJob } from '../../hooks/useProjectIngestJob';
import { useSynthesisData } from './useSynthesisData';
import { SynthesisProvider } from './SynthesisContext';
import { downloadJSON, printNode } from './exportUtils';
import type { SynthesisPayload } from './types';
import KeyThemes from './tabs/KeyThemes';
import Quotes from './tabs/Quotes';
import Coverage from './tabs/Coverage';
import Insights from './tabs/Insights';
import Journey from './tabs/Journey';
import WorkflowArchitecture from './tabs/WorkflowArchitecture';
import MindMap from './tabs/MindMap';
import FirstPrinciples from './tabs/FirstPrinciples';
import ExecSummary from './tabs/ExecSummary';
import ByStakeholder from './tabs/ByStakeholder';
import { countOf } from '../../utils/pluralize';

interface TabDef {
  id: string;
  label: string;
  icon: LucideIcon;
  count?: number;
}
interface TabGroupDef {
  label: string;
  tabs: TabDef[];
}

function TabBody({ id, seen, markSeen }: {
  id: string; seen: Set<string>; markSeen: (id: string) => void;
}) {
  switch (id) {
    case 'exec-summary':     return <ExecSummary />;
    case 'key-themes':       return <KeyThemes />;
    case 'by-stakeholder':   return <ByStakeholder />;
    case 'insights':         return <Insights />;
    case 'quotes':           return <Quotes />;
    case 'journey':          return <Journey proposalSeen={seen.has('journey')} onConfirmProposal={() => markSeen('journey')} />;
    case 'mind-map':         return <MindMap />;
    case 'architecture':     return <WorkflowArchitecture proposalSeen={seen.has('architecture')} onConfirmProposal={() => markSeen('architecture')} />;
    case 'first-principles': return <FirstPrinciples />;
    case 'coverage':         return <Coverage />;
    default:                 return null;
  }
}


// Turn a raw backend failure string into a short, user-friendly message. The backend errors can be
// long and internal (temp file paths, stack-ish text) — a consultant shouldn't see those, and they
// read as broken/unprofessional. Timeouts are called out specifically because they're actionable
// (use a smaller batch). The full raw error is still logged on the backend for developers to trace.
function friendlyRunError(raw: string | null | undefined, verb = 'run'): string {
  const text = (raw || '').trim();
  if (!text) return `The ${verb} didn’t finish. Please try again.`;
  if (/timed out|timeout/i.test(text)) {
    return `The ${verb} took too long and timed out. Large documents are split across parallel workers — retry and leave this page open while it runs.`;
  }
  return `The ${verb} didn’t finish. Please try again — if it keeps failing, contact support.`;
}

export default function SynthesisVisualizations({ projectId }: { projectId?: string | null }) {
  // Live payload for this project. Until a synthesis is stored, source is 'empty' and we show an
  // onboarding state (Generate CTA) instead of a demo — the payload below is only a structural
  // placeholder so destructuring never crashes; it is never rendered while !isLive.
  const {
    payload, source, loading: synthesisDataLoading, lastRun, runHistory, sourceCount, hasUnsynthesizedSources,
    applyPayload, refresh: refreshSynthesisData,
  } = useSynthesisData(projectId);
  const isLive = source === 'live';
  const { engagement, sources, themes, insights, opportunities, quotes, journey, coverage, meta } = payload;

  const [active, setActive] = useState('exec-summary');
  const [quoteFilter, setQuoteFilter] = useState<number | 'all'>('all');
  const [expandedThemeRank, setExpandedThemeRank] = useState<number | null>(null);
  // Ref to the active tab's content, so per-tab Export can print exactly that tab.
  const tabRef = useRef<HTMLDivElement>(null);
  // Import synthesis JSON — the safe "get data in" path: upload a synthesis payload (produced by the
  // interim skill), persist it to this project's snapshot, and render it immediately. Only touches
  // the synthesis_viz_snapshots table via the existing route — no engine / pipeline involved.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  // Run/Rerun Synthesis: runs the synthesis stage over whatever transcripts are already ingested.
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState<number | null>(null);
  // The jobId of whichever run is currently polling, plus a ref mirror so the poll loop (a plain
  // for-loop, not re-rendered) can check "did the user cancel?" without depending on stale closure
  // state. cancelling disables the Cancel button itself while the DELETE request is in flight.
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const cancelledRef = useRef(false);
  // Upload transcripts (decoupled from synthesis, per the client's "no prompt required" ask): the
  // backend ingests them through the DTSE engine only — no synthesis runs as part of this action.
  // Uses the same background-job polling as Run. Gated server-side (SYNTHESIS_REAL_INGEST) — a clear
  // message shows if it isn't enabled in this environment yet.
  const genInputRef = useRef<HTMLInputElement>(null);
  const ingestJob = useProjectIngestJob();
  const [ingesting, setIngesting] = useState(false);
  const [ingestProgress, setIngestProgress] = useState<number | null>(null);

  useEffect(() => {
    if (projectId) void ingestJob.resume(projectId);
  }, [projectId]); // eslint-disable-line

  useEffect(() => {
    setIngesting(ingestJob.ingesting);
    setIngestProgress(ingestJob.ingesting ? ingestJob.snapshot.progress : null);
    if (ingestJob.snapshot.state === 'completed') refreshSynthesisData();
  }, [ingestJob.ingesting, ingestJob.snapshot.progress, ingestJob.snapshot.state, refreshSynthesisData]);
  // Run-history panel: collapsed by default, plus which past run is currently being loaded (so its
  // row can show a spinner while its payload is fetched on demand), and which non-completed row is
  // expanded in place to show its full detail (files + error) — there's no payload to "open" for a
  // failed/cancelled run, so clicking it toggles an inline detail block instead of navigating away.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  // First-render proposal gate, tracked here so it fires once per session per tab
  // and survives tab switches (each tab remounts on switch).
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const markSeen = (id: string) => setSeen((s) => { const n = new Set(s); n.add(id); return n; });

  // Revisit a past run from the history list. A completed run has a stored payload — load it via the
  // same GET /run/:jobId the poller uses (now reading back THAT run's own snapshot, not just the
  // project's latest one — see synthesisViz.js describeJob) and render it. A non-completed run has
  // no payload to show (it never produced one), so clicking it instead toggles an inline detail
  // block showing the uploaded files + a friendly error, rather than being a dead click.
  const handleOpenHistoricalRun = async (jobId: string, state: string) => {
    if (state !== 'completed') {
      setExpandedRunId((cur) => (cur === jobId ? null : jobId));
      return;
    }
    if (projectId == null) return;
    setLoadingRunId(jobId);
    const pid = encodeURIComponent(String(projectId));
    try {
      const res = await apiClient.get(`/api/synthesis-viz/${pid}/run/${encodeURIComponent(jobId)}`);
      const s = res?.data || {};
      if (s.state === 'completed' && s.payload && Array.isArray(s.payload.themes)) {
        applyPayload(s.payload as SynthesisPayload);
        setImportError(null); // clear any stale error from an earlier failed attempt (this run succeeded)
        setHistoryOpen(false);
      } else {
        setImportError('That run’s results are no longer available.');
      }
    } catch {
      setImportError('Could not load that run.');
    } finally {
      setLoadingRunId(null);
    }
  };

  // Human-readable timestamp for a history row (locale string; falls back gracefully).
  const fmtRunTime = (ts: number | null) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString(); } catch { return ''; }
  };

  const NAV_GROUPS: TabGroupDef[] = [
    {
      label: 'Summary',
      tabs: [{ id: 'exec-summary', label: 'Executive Summary', icon: FileText }],
    },
    {
      label: 'What We Found',
      tabs: [
        { id: 'key-themes', label: 'Key Themes', icon: Activity, count: themes.length },
        { id: 'by-stakeholder', label: 'By Stakeholder', icon: Users, count: new Set(quotes.map((q) => String(q.who || '').trim()).filter(Boolean)).size || undefined },
        { id: 'quotes', label: 'Evidence', icon: Quote, count: quotes.length },
      ],
    },
    {
      label: 'Why It Matters',
      tabs: [
        { id: 'journey', label: 'User Journey', icon: Navigation, count: journey.length },
        { id: 'architecture', label: 'Root Cause Analysis', icon: Layers },
        { id: 'first-principles', label: 'First Principles', icon: Atom },
      ],
    },
    {
      label: 'What To Do',
      tabs: [{ id: 'insights', label: 'Actions & Opportunities', icon: Lightbulb, count: insights.length + opportunities.length }],
    },
    {
      label: 'Additional Exploration',
      tabs: [
        { id: 'mind-map', label: 'Mind Map', icon: Share2 },
        { id: 'coverage', label: 'Interview Coverage', icon: Users, count: coverage.length || undefined },
      ],
    },
  ];
  const TABS: TabDef[] = NAV_GROUPS.flatMap((g) => g.tabs);

  const slug = (engagement.client || 'synthesis').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
  // Module-level Export: download the exact JSON the tabs render — a real artifact, not a dead button.
  const handleExport = () => downloadJSON(payload, `synthesis-visualizations-${slug || 'export'}.json`);
  // Per-tab Export: print just the active tab (browser → Save as PDF) for a client-ready page.
  const activeLabel = TABS.find((t) => t.id === active)?.label || 'Tab';
  const handleTabExport = () => printNode(tabRef.current, `${engagement.client} — ${activeLabel}`);

  // Upload transcripts and ingest them into DTSE ONLY — no synthesis runs as part of this action.
  // Decoupled from Run/Rerun Synthesis below (per the client's "no prompt required" ask): this
  // handler never applies a payload (an ingest job produces none) and never touches `running`
  // (Run Synthesis stays independently clickable while an upload is still in flight, or vice versa).
  const handleIngestFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (!list.length) return;
    if (projectId == null) { setImportError('Open a project first to upload transcripts.'); return; }
    setImportError(null);
    const snap = await ingestJob.startFiles(projectId, list);
    if (snap.state === 'failed') setImportError(snap.error || 'Upload failed.');
    else refreshSynthesisData();
  };

  // Run/Rerun Synthesis: a plain click, no prompt, no file picker — synthesizes over everything
  // already ingested for this project. This is what the client's "click the button" ask maps to.
  const handleSynthesize = async () => {
    if (projectId == null) { setImportError('Open a project first to run synthesis.'); return; }
    setRunning(true);
    setRunProgress(0);
    setImportError(null);
    cancelledRef.current = false;
    const pid = encodeURIComponent(String(projectId));
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    try {
      const res = await apiClient.post(`/api/synthesis-viz/${pid}/synthesize`, {});
      const jobId = res?.data?.jobId;
      if (!jobId) { setImportError('Synthesis did not start.'); return; }
      setCurrentJobId(String(jobId));
      for (let i = 0; i < 400; i++) {
        await sleep(3000);
        if (cancelledRef.current) return; // handleCancel already set the user-facing message AND refreshed
        let st;
        try { st = await apiClient.get(`/api/synthesis-viz/${pid}/run/${encodeURIComponent(String(jobId))}`); }
        catch { continue; }
        const s = st?.data || {};
        if (typeof s.progress === 'number') setRunProgress(s.progress);
        if (s.state === 'completed') {
          if (s.payload && Array.isArray(s.payload.themes)) {
            applyPayload(s.payload as SynthesisPayload);
            setImportError(null);
          } else {
            setImportError('Synthesis finished but returned no themes.');
          }
          refreshSynthesisData();
          return;
        }
        if (s.state === 'cancelled') { setImportError('Cancelled.'); refreshSynthesisData(); return; }
        if (s.state === 'failed') { setImportError(friendlyRunError(s.error, 'synthesis')); refreshSynthesisData(); return; }
        if (s.state === 'unknown') { setImportError('Synthesis run not found — it may have expired.'); return; }
      }
      setImportError('Synthesis is taking longer than expected — check back shortly.');
    } catch (err: any) {
      setImportError(err?.response?.data?.error || (err instanceof Error ? err.message : 'Synthesis failed.'));
    } finally {
      setRunning(false);
      setRunProgress(null);
      setCurrentJobId(null);
    }
  };

  // Cancel the currently-running Generate/Run job. Calls the DELETE endpoint (which flags the job
  // for the worker to stop at its next checkpoint, or removes it outright if still queued), then
  // stops this component's own poll loop immediately rather than waiting for the next 3s tick to
  // see a 'failed' state — the user gets instant feedback even though the backend job may take a
  // few more seconds to actually unwind (it finishes whatever LLM call is already in flight).
  const handleCancel = async () => {
    if (!currentJobId || projectId == null) return;
    setCancelling(true);
    const pid = encodeURIComponent(String(projectId));
    try {
      await apiClient.delete(`/api/synthesis-viz/${pid}/run/${encodeURIComponent(currentJobId)}`);
      cancelledRef.current = true;
      setImportError('Cancelled.');
      refreshSynthesisData(); // Run History doesn't otherwise see this cancellation until a remount
    } catch (err: any) {
      setImportError(err?.response?.data?.error || 'Failed to cancel — the run may already be finishing.');
    } finally {
      setCancelling(false);
      setRunning(false);
      setRunProgress(null);
      setCurrentJobId(null);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';                 // let the same file be re-selected later
    if (!file) return;
    setImporting(true);
    setImportError(null);
    try {
      const json = JSON.parse(await file.text());
      if (!json || !Array.isArray(json.themes)) {
        throw new Error('That file is not a synthesis payload — no themes[] found.');
      }
      // Persist to this project's snapshot (skipped when there is no project — preview only).
      if (projectId != null) {
        await apiClient.put(`/api/synthesis-viz/${encodeURIComponent(String(projectId))}`, { payload: json });
      }
      applyPayload(json as SynthesisPayload);   // render it immediately
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <SynthesisProvider
      payload={payload}
      projectId={projectId != null ? String(projectId) : undefined}
      applyPayload={applyPayload}
      nav={{ active, setActive, quoteFilter, setQuoteFilter, expandedThemeRank, setExpandedThemeRank }}
    >
    {/* Full-bleed: fill the AI Council content area (like User Research Synthesis) instead of
        sitting in a narrow 960px column — this is a full 10-tab dashboard. The parent card already
        supplies the outer gutter (p-6), so only a small vertical rhythm is added here. */}
    <div style={{ width: '100%', padding: '4px 0 8px', fontFamily: 'inherit' }}>
      {/* No breadcrumb here — the Strategy Synthesis tiles above already show where you are. */}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flex: '1 1 420px', minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: color.iconGrad,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Share2 size={18} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: color.ink }}>
              Synthesis Visualizations
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: 14, color: color.textMuted, lineHeight: 1.5, maxWidth: 640 }}>
              {isLive ? (
                <>
                  {engagement.subtitle} — {countOf(engagement.interviews, 'stakeholder interview')} for{' '}
                  <strong style={{ color: color.text }}>{engagement.client}</strong>.
                </>
              ) : (
                <>Upload your interview transcripts, then click <strong style={{ color: color.text }}>Run Synthesis</strong> to build themes, insights, journey, and more — no description needed. Run Synthesis uses the same themes as User Research Synthesis, the findings the most interviews raised. It does not re-read the original files.</>
              )}
              {sourceCount > 0 && (
                <span style={{ display: 'block', marginTop: 6 }}>
                  {sourceCount} transcript{sourceCount === 1 ? '' : 's'} already ingested — Run Synthesis is the remaining (faster) step.
                </span>
              )}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: color.textMuted,
            border: `1px solid ${color.hairline}`, borderRadius: 8, padding: '6px 10px',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: color.green }} />
            Synthesis engine ready
          </span>
          {/* The legacy findings-JSON "Run synthesis" upload used to render its own button here,
              wired to the /run route — it read as a second, confusing "run synthesis" action next
              to Upload transcripts / Run Synthesis below, both doing the same thing from a user's
              perspective. Removed (handler + backend /run route are untouched, kept for any
              offline skill that still produces pre-computed findings.json bundles — just no UI
              entry point). Single clear action: Upload transcripts, then Run/Rerun Synthesis. */}
          <input
            ref={genInputRef}
            type="file"
            multiple
            accept=".txt,.md,.pdf,.doc,.docx"
            onChange={handleIngestFiles}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => genInputRef.current?.click()}
            disabled={ingesting}
            title="Upload interview transcripts — ingests them for synthesis, does not run synthesis"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: ingesting ? 'wait' : 'pointer',
              padding: '8px 12px', borderRadius: 8, background: color.ink, color: '#fff', border: 'none',
            }}
          >
            <Upload size={14} /> {ingesting
              ? (ingestJob.snapshot.progressDetail?.filename
                ? `${ingestJob.snapshot.progressDetail.current}/${ingestJob.snapshot.progressDetail.total} · ${ingestJob.snapshot.progressDetail.filename}`
                : (ingestProgress != null ? `Uploading… ${ingestProgress}%` : 'Uploading…'))
              : 'Upload transcripts'}
          </button>
          {ingesting && projectId && (
            <button
              onClick={() => void ingestJob.cancel(projectId)}
              title="Stop ingest — finished interviews stay in the project"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                padding: '8px 12px', borderRadius: 8, background: 'transparent', color: color.text,
                border: `1px solid ${color.hairline}`,
              }}
            >
              <Square size={14} /> Stop
            </button>
          )}
          <button
            onClick={handleSynthesize}
            disabled={running || sourceCount === 0}
            title={sourceCount === 0 ? 'Upload transcripts first' : 'Run synthesis over the ingested transcripts — no prompt needed'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
              background: (running || sourceCount === 0) ? color.hairline : color.purpleGrad,
              color: (running || sourceCount === 0) ? color.textFaint : '#fff',
              border: 'none', borderRadius: 8, padding: '8px 12px',
              cursor: running ? 'wait' : sourceCount === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            <Sparkles size={14} /> {running
              ? (runProgress != null ? `Synthesizing… ${runProgress}%` : 'Synthesizing…')
              : hasUnsynthesizedSources ? 'Rerun Synthesis' : 'Run Synthesis'}
          </button>
          {running && currentJobId && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              title="Stop this run — halts further LLM calls and marks it as cancelled"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
                background: color.surface, color: '#B91C1C', border: '1px solid #FCA5A5',
                borderRadius: 8, padding: '7px 12px', cursor: cancelling ? 'wait' : 'pointer',
              }}
            >
              <X size={14} /> {cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            title="Import a synthesis JSON (from the interim skill) into this project"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
              background: color.surface, color: color.text, border: `1px solid ${color.hairline}`,
              borderRadius: 8, padding: '7px 12px', cursor: importing ? 'wait' : 'pointer',
            }}
          >
            <Upload size={14} /> {importing ? 'Importing…' : 'Import JSON'}
          </button>
          <button onClick={handleExport} title="Download the synthesis JSON" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
            background: color.surface, color: color.text, border: `1px solid ${color.hairline}`,
            borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
          }}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {importError && (
        <div style={{
          marginTop: 12, fontSize: 12.5, color: '#B91C1C',
          background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '7px 12px',
        }}>
          ⚠ {importError}
        </div>
      )}

      {/* Run history (DoD "ideally a history list"): recent runs for this project, most-recent first.
          Collapsed by default so it never gets in the way; a completed run is clickable to re-load
          its results, a failed/cancelled run shows its outcome. Only rendered when runs exist. */}
      {runHistory.length > 0 && (
        <div style={{ marginTop: 12, border: `1px solid ${color.hairline}`, borderRadius: 10, overflow: 'hidden' }}>
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, fontSize: 12.5, fontWeight: 600, color: color.text, background: color.surfaceAlt,
              border: 'none', padding: '9px 14px', cursor: 'pointer',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Activity size={14} /> Run history <span style={{ color: color.textFaint, fontWeight: 400 }}>({runHistory.length})</span>
            </span>
            <span style={{ color: color.textFaint, fontSize: 11 }}>{historyOpen ? 'Hide' : 'Show'}</span>
          </button>
          {historyOpen && (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {runHistory.map((r) => {
                const isDone = r.state === 'completed';
                const badge =
                  r.state === 'completed' ? { label: 'Completed', bg: '#ECFDF5', fg: '#047857' }
                  : r.state === 'failed' ? { label: 'Failed', bg: '#FEF2F2', fg: '#B91C1C' }
                  : r.state === 'cancelled' ? { label: 'Cancelled', bg: '#FEF3C7', fg: '#92400E' }
                  : { label: 'Running', bg: '#EFF6FF', fg: '#1D4ED8' };
                const isExpanded = expandedRunId === r.jobId;
                return (
                  <div key={r.jobId}>
                    <div
                      onClick={() => handleOpenHistoricalRun(r.jobId, r.state)}
                      title={isDone ? 'Load this run’s results' : 'Click for details'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                        borderTop: `1px solid ${color.hairline}`, fontSize: 12.5,
                        cursor: isDone ? (loadingRunId === r.jobId ? 'wait' : 'pointer') : 'pointer',
                        background: isExpanded ? color.surfaceAlt : color.surface,
                      }}
                    >
                      <span style={{
                        fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                        background: badge.bg, color: badge.fg, whiteSpace: 'nowrap',
                      }}>{badge.label}</span>
                      <span style={{ color: color.textMuted, whiteSpace: 'nowrap' }}>{fmtRunTime(r.timestamp)}</span>
                      <span style={{ color: color.textFaint, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.state === 'failed'
                          ? (/timed out|timeout/i.test(r.error || '') ? 'Timed out' : 'Did not finish')
                          : r.state === 'cancelled' ? 'Cancelled by user'
                          : isDone ? 'Click to load results'
                          : 'In progress'}
                      </span>
                      {isDone && (
                        <span
                          onClick={(e) => { e.stopPropagation(); setExpandedRunId((cur) => (cur === r.jobId ? null : r.jobId)); }}
                          title="Show the files used in this run"
                          style={{ display: 'inline-flex', padding: 2, cursor: 'pointer' }}
                        >
                          <Info size={14} style={{ color: isExpanded ? color.purple : color.textFaint }} />
                        </span>
                      )}
                      {isDone ? (
                        loadingRunId === r.jobId
                          ? <span style={{ width: 13, height: 13, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block' }} className="animate-spin" />
                          : <ChevronRight size={14} style={{ color: color.textFaint }} />
                      ) : (
                        <ChevronRight size={14} style={{ color: color.textFaint, transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                      )}
                    </div>
                    {isExpanded && (
                      <div style={{
                        padding: '10px 14px 12px 14px', background: color.surfaceAlt,
                        borderTop: `1px solid ${color.hairline}`, fontSize: 12,
                      }}>
                        {r.files.length > 0 && (
                          <div style={{ marginBottom: r.error ? 8 : 0 }}>
                            <div style={{ color: color.textFaint, fontWeight: 600, marginBottom: 3 }}>Files in this run</div>
                            {r.files.map((f) => (
                              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, color: color.textMuted, padding: '2px 0' }}>
                                <FileText size={12} /> {f}
                              </div>
                            ))}
                          </div>
                        )}
                        {r.state === 'failed' && (
                          <div style={{ color: '#B91C1C' }}>
                            {friendlyRunError(r.error, 'run')}
                          </div>
                        )}
                        {r.state === 'cancelled' && (
                          <div style={{ color: '#92400E' }}>Cancelled by user.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isLive ? (
      <>
      {/* New transcripts have been uploaded since the last synthesis ran — surface this above
          Sources, right where the results start, not buried at the bottom, per the client's "just
          refresh or click a Rerun button" ask. */}
      {hasUnsynthesizedSources && (
        <div style={{
          marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, fontSize: 12.5, color: color.purpleDeep, background: color.pillActiveBg,
          border: `1px solid ${color.chipBorder}`, borderRadius: 8, padding: '8px 12px',
        }}>
          <span>New transcripts have been uploaded since this synthesis ran.</span>
          <button
            onClick={handleSynthesize}
            disabled={running}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600,
              background: running ? color.hairline : color.purpleGrad, color: running ? color.textFaint : '#fff',
              border: 'none', borderRadius: 8, padding: '6px 12px', cursor: running ? 'wait' : 'pointer',
            }}
          >
            <Sparkles size={12} /> {running
              ? (runProgress != null ? `Synthesizing… ${runProgress}%` : 'Synthesizing…')
              : 'Rerun Synthesis'}
          </button>
        </div>
      )}

      {/* Sources */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '18px 0 20px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>SOURCES</span>
        {sources.map((s) => (
          <span key={s.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
            background: s.role === 'context' ? color.pillMutedBg : color.pillActiveBg,
            color: s.role === 'context' ? color.textMuted : color.purpleDeep,
            border: `1px solid ${s.role === 'context' ? color.hairline : color.chipBorder}`,
            borderRadius: 999, padding: '4px 10px',
          }}>
            <FileText size={13} /> {s.file}
            {s.role === 'context' && <span style={{ fontSize: 10, opacity: 0.7 }}>context</span>}
            <X size={13} style={{ opacity: 0.6, cursor: 'pointer' }} />
          </span>
        ))}
        <span style={{ fontSize: 13, color: color.purple, cursor: 'pointer' }}>+ Add source</span>
      </div>

      {/* Tab card */}
      <div style={{ border: `1px solid ${color.hairline}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', background: color.surfaceAlt, borderBottom: `1px solid ${color.hairline}` }}>
          <div style={{ display: 'flex', minWidth: 'max-content' }}>
            {NAV_GROUPS.map((group, idx) => (
              <div
                key={group.label}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: '8px 4px 0',
                  borderRight: idx < NAV_GROUPS.length - 1 ? `1px solid ${color.hairline}` : 'none',
                }}
              >
                <div style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: color.textFaint,
                  padding: '0 12px 4px',
                }}>
                  {group.label}
                </div>
                <div style={{ display: 'flex' }}>
                  {group.tabs.map((t) => {
                    const on = active === t.id;
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setActive(t.id)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
                          padding: '12px 12px', fontSize: 13, cursor: 'pointer', background: 'transparent',
                          border: 'none', borderBottom: `2px solid ${on ? color.purple : 'transparent'}`,
                          color: on ? color.purple : color.textMuted, fontWeight: on ? 600 : 500,
                          marginBottom: -1,
                        }}
                      >
                        <Icon size={15} />
                        {t.label}
                        {t.count != null && (
                          <span style={{
                            fontSize: 11, fontFamily: mono, background: color.pillActiveBg,
                            color: color.purpleDeep, borderRadius: 6, padding: '1px 6px',
                          }}>{t.count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '28px 32px 36px', position: 'relative' }}>
          <button
            onClick={handleTabExport}
            title="Export this tab — Print / Save as PDF"
            style={{
              position: 'absolute', top: 12, right: 16, zIndex: 2,
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
              background: color.surface, color: color.textMuted, border: `1px solid ${color.hairline}`,
              borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
            }}
          >
            <Download size={13} /> Export tab
          </button>
          {/* The button sits OUTSIDE this ref, so it never prints into the exported page. */}
          <div ref={tabRef}>
            <TabBody id={active} seen={seen} markSeen={markSeen} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        marginTop: 12, fontSize: 12, color: color.textFaint,
      }}>
        <span>
          Synthesized by {meta.model_tier}
          {meta.synthesized_at ? ` · ${meta.synthesized_at}` : ''}
          {' · '}
          <span onClick={handleSynthesize} style={{ color: color.purple, cursor: running ? 'wait' : 'pointer' }}>
            {running ? 'Synthesizing…' : 'Recompute'}
          </span>
        </span>
        <span>
          {countOf(engagement.interviews, 'interview')}
          {engagement.regions > 0 ? ` · ${countOf(engagement.regions, 'region')}` : ''}
          {engagement.window ? ` · ${engagement.window}` : ''}
        </span>
      </div>
      </>
      ) : lastRun && (lastRun.state === 'failed' || lastRun.state === 'cancelled') ? (
        /* A run was attempted for this project and did NOT complete — show what actually happened
           instead of the plain "No synthesis yet" state, which is indistinguishable from never
           having tried at all (the bug this branch fixes). No stored payload exists (a failed run
           never writes one), so this is still the empty-state layout, just with the real outcome
           and a Retry affordance instead of a generic first-time prompt. */
        <div style={{
          marginTop: 24, border: `1px solid #FCA5A5`, borderRadius: 14,
          padding: '56px 24px', textAlign: 'center', background: '#FEF2F2',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: '#FEE2E2',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <X size={22} color="#B91C1C" />
          </div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>
            {lastRun.state === 'cancelled' ? 'Last run was cancelled' : 'Last run failed'}
          </h3>
          <p style={{ margin: '8px auto 20px', fontSize: 14, color: '#B91C1C', maxWidth: 480, lineHeight: 1.5 }}>
            {/* Short, user-friendly summary via the shared helper — NOT the raw backend error (which
                can be a long internal message with temp file paths). The full raw error is logged on
                the backend for developers to trace (see synthesisWorker.js 'failed' handler). */}
            {lastRun.state === 'cancelled' ? 'Cancelled by user.' : friendlyRunError(lastRun.error, 'run')}
            {typeof lastRun.progress === 'number' && lastRun.progress > 0 && ` (stopped at ${lastRun.progress}%)`}
          </p>
          {projectId && (
            // Sources are already ingested for any run that got far enough to fail/be cancelled
            // (ingest happens as its own step now) — Retry means synthesize again, not re-upload.
            // If somehow zero sources are ingested (e.g. an old pre-decoupling failed run), fall
            // back to opening the file picker instead.
            <button
              onClick={() => (sourceCount > 0 ? handleSynthesize() : genInputRef.current?.click())}
              disabled={running}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600,
                background: running ? color.hairline : color.purpleGrad, color: running ? color.textFaint : '#fff',
                border: 'none', borderRadius: 10, padding: '11px 20px', cursor: running ? 'wait' : 'pointer',
              }}
            >
              <Sparkles size={16} /> {sourceCount > 0 ? 'Retry Synthesis' : 'Retry — upload transcripts'}
            </button>
          )}
        </div>
      ) : lastRun && (lastRun.state === 'active' || lastRun.state === 'waiting' || lastRun.state === 'delayed') ? (
        /* A run is still in flight from a prior page load — reconnecting the poll loop is a larger
           change (it needs the jobId + the same polling logic handleSynthesize already owns); for
           now, tell the user it's running rather than showing the misleading "No synthesis yet", and
           let them check back once it finishes. */
        <div style={{
          marginTop: 24, border: `1px dashed ${color.hairline}`, borderRadius: 14,
          padding: '56px 24px', textAlign: 'center', background: color.surfaceAlt,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: color.iconGrad,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <Sparkles size={22} />
          </div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: color.ink }}>
            A run is still in progress{typeof lastRun.progress === 'number' && lastRun.progress > 0 ? ` (${lastRun.progress}%)` : ''}
          </h3>
          <p style={{ margin: '8px auto 20px', fontSize: 14, color: color.textMuted, maxWidth: 440, lineHeight: 1.5 }}>
            A previous run for this project is still going in the background. Refresh in a bit to check on it.
          </p>
          <button
            onClick={refreshSynthesisData}
            disabled={synthesisDataLoading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600,
              background: color.surface, color: color.text, border: `1px solid ${color.hairline}`,
              borderRadius: 10, padding: '11px 20px', cursor: synthesisDataLoading ? 'wait' : 'pointer',
            }}
          >
            {synthesisDataLoading ? 'Checking…' : 'Check again'}
          </button>
        </div>
      ) : (
        /* Empty state — no synthesis stored yet, and no non-completed run found either. Two
           variants depending on whether transcripts have already been uploaded: zero sources means
           genuinely never started (prompt to upload); sources > 0 means the upload step is done and
           only the plain Run click is left (per the client's decoupled "upload once, click to run,
           no prompt" ask). No demo data either way. */
        <div style={{
          marginTop: 24, border: `1px dashed ${color.hairline}`, borderRadius: 14,
          padding: '56px 24px', textAlign: 'center', background: color.surfaceAlt,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: color.iconGrad,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <Sparkles size={22} />
          </div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: color.ink }}>
            {sourceCount > 0 ? `${countOf(sourceCount, 'transcript')} ready` : 'No synthesis yet'}
          </h3>
          <p style={{ margin: '8px auto 20px', fontSize: 14, color: color.textMuted, maxWidth: 440, lineHeight: 1.5 }}>
            {!projectId
              ? 'Select a project first, then upload interview transcripts to build your synthesis.'
              : sourceCount > 0
              ? 'Click Run Synthesis to rebuild themes from stored interviews — it does not re-read the original files.'
              : 'Upload your interview transcripts to build themes, insights, a journey map, mind map and more for this project.'}
          </p>
          {projectId && (
            <button
              onClick={() => (sourceCount > 0 ? handleSynthesize() : genInputRef.current?.click())}
              disabled={sourceCount > 0 ? running : ingesting}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600,
                background: (sourceCount > 0 ? running : ingesting) ? color.hairline : color.purpleGrad,
                color: (sourceCount > 0 ? running : ingesting) ? color.textFaint : '#fff',
                border: 'none', borderRadius: 10, padding: '11px 20px',
                cursor: (sourceCount > 0 ? running : ingesting) ? 'wait' : 'pointer',
              }}
            >
              <Sparkles size={16} /> {sourceCount > 0 ? 'Run Synthesis' : 'Upload transcripts'}
            </button>
          )}
        </div>
      )}
    </div>
    </SynthesisProvider>
  );
}
